<template>
  <div>
    <b-alert show variant="warning" dismissible>
      <p>
        This feature is a work in progress. It may not work properly, and files
        <strong>may stop working at any time</strong>.
      </p>
      <p>
        If you encounter any issues, please
        <router-link :to="{ name: 'Contact' }" class="alert-link"
          >let me know</router-link
        >
        so I can try to fix them.
      </p>
    </b-alert>

    <p>
      This will generate a file that you can put in your Tabletop Simulator's
      "Saved Objects" folder. It includes:
    </p>
    <ul>
      <li>Main deck</li>
      <li>Character card(s)</li>
      <li>Rule card(s)</li>
      <ul>
        <li>
          All cards are named and have descriptions that are visible on hover
        </li>
      </ul>
      <li>Card back images (add an image URL in the "Appearance" section)</li>
      <li>Hero pawn(s)</li>
      <li>Sidekick token(s)</li>
      <li>Health counters for all multi-health characters</li>
    </ul>

    <b-form-group
      description="If left blank card backs will be a solid colour"
      label="Card back image (optional)"
    >
      <b-form-input
        :placeholder="cardbackUrlPlaceholder"
        v-model="cardbackUrl"
        trim
      >
      </b-form-input>
    </b-form-group>
    <b-form-row>
      <b-col cols="3">
        <b-form-group label="Language">
          <language-picker v-model="language"></language-picker>
        </b-form-group>
      </b-col>
    </b-form-row>

    <p class="text-warning">
      Decks may take some time to load initially in TTS, particularly decks with
      card art, since the server needs to download and convert the images. After
      first load the image should be cached and it will load faster.
    </p>
    <p class="text-warning">
      Changes to your deck will not be picked up automatically; you will need to
      generate a new JSON file.
    </p>
    <b-alert
      show
      variant="warning"
      v-for="(warning, idx) in warnings"
      :key="`warning-message-${idx}`"
      text-variant="warning"
    >
      {{ warning }}
    </b-alert>
    <b-button :href="dataStr" :download="filename">Download</b-button>
  </div>
</template>

<script>
import api from "@/api";
import checkEmoji from "@/mixins/checkEmoji.js";
import LanguagePicker from "@/components/LanguagePicker.vue"

const hexRgb = require("hex-rgb");
import { best } from "wcag-color";

export default {
  name: "TTSExport",
  components: {LanguagePicker},
  props: {
    versionID: {
      type: String,
      required: true,
    },
    deck: {
      type: Object,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      cols: 10,
      cardWidth: process.env.VUE_APP_TTS_EXPORT_WIDTH || 550,
      cardbackUrl: "",
      cacheBuster: Date.now(),
      apiUrlBase:
        process.env.NODE_ENV === "production"
          ? "https://unmatched.cards"
          : "http://localhost:5000",
      language: "en", 
    };
  },
  methods: {
    getCharacterNickname(character) {
      var nickname = character.hero.name;
      if (character.sidekick.quantity) {
        nickname += "\n";
        nickname += character.sidekick.name;
        if (character.sidekick.quantity > 1) {
          nickname += ` x${character.sidekick.quantity}`;
        }
      }
      return nickname;
    },
    getCharacterDescription(character) {
      return `Move: ${character.hero.move}\n\nSpecial abilities:\n${character.hero.specialAbility}`;
    },
    generateDeck(deckID) {
      const customDeck = {
        Name: "DeckCustom",
        Transform: {
          posX: 0,
          posY: 0,
          posZ: 0,
          rotX: 0,
          rotY: 180.0,
          rotZ: 180.0,
          scaleX: 1.0,
          scaleY: 1.0,
          scaleZ: 1.0,
        },
        Nickname: this.name,
        Description: "",
        GMNotes: "",
        ColorDiffuse: {
          r: 0.713235259,
          g: 0.713235259,
          b: 0.713235259,
        },
        Locked: false,
        Grid: true,
        Snap: true,
        IgnoreFoW: false,
        MeasureMovement: false,
        DragSelectable: true,
        Autoraise: true,
        Sticky: true,
        Tooltip: true,
        GridProjection: false,
        HideWhenFaceDown: true,
        Hands: false,
        SidewaysCard: false,
        CustomDeck: {},
        DeckIDs: [],
        LuaScript: "",
        LuaScriptState: "",
        XmlUI: "",
        ContainedObjects: [],
      };

      customDeck.CustomDeck[`${deckID}`] = {
        FaceURL: `${this.apiUrlBase}/api/card_img/${this.versionID}?width=${this.cardWidth}&columns=${this.cols}&duplicates=false&cache=${this.cacheBuster}&language=${this.language}`,
        BackURL:
          this.cardbackUrl ||
          this.deck.appearance.cardbackUrl ||
          `https://singlecolorimage.com/get/${this.deck.appearance.highlightColour.substring(
            1
          )}/1x1`,
        NumWidth: this.cols,
        NumHeight: this.rows,
        BackIsHidden: true,
        UniqueBack: false,
        Type: 0,
      };

      var cardCount = 0;
      var startID = deckID * 100;
      this.deck.cards.forEach((card) => {
        const cardID = startID + cardCount;
        for (var i = 0; i < card.quantity; i++) {
          const cardJson = {
            Name: "Card",
            Transform: {
              posX: 0,
              posY: 0,
              posZ: 0,
              rotX: 0,
              rotY: 180.0,
              rotZ: 180.0,
              scaleX: 1.0,
              scaleY: 1.0,
              scaleZ: 1.0,
            },
            Nickname: card.title,
            Description: card.description,
            GMNotes: "",
            ColorDiffuse: {
              r: 0.713235259,
              g: 0.713235259,
              b: 0.713235259,
            },
            Locked: false,
            Grid: true,
            Snap: true,
            IgnoreFoW: false,
            MeasureMovement: false,
            DragSelectable: true,
            Autoraise: true,
            Sticky: true,
            Tooltip: true,
            GridProjection: false,
            Hands: true,
            CardID: cardID,
            SidewaysCard: false,
            LuaScript: "",
            LuaScriptState: "",
            XmlUI: "",
            ContainedObjects: [],
          };
          customDeck.ContainedObjects.push(cardJson);
          customDeck.DeckIDs.push(cardID);
        }
        cardCount++;
      });

      return customDeck;
    },
    generateCharacterCards(deckID, ObjectStates) {
      const customDeck = {};

      const characterDeckUrl = `${this.apiUrlBase}/api/card_img/${this.versionID}?width=${this.cardWidth}&deck=0&characters=1&columns=${this.cols}&cache=${this.cacheBuster}&language=${this.language}`;
      customDeck[`${deckID}`] = {
        FaceURL: characterDeckUrl,
        BackURL:
          "http://cloud-3.steamusercontent.com/ugc/1026203121196920886/DF4F3A26991DD9A2C2F58A2E6AA0094AD59F660A/",
        NumWidth: this.cols,
        NumHeight: this.charRows,
        BackIsHidden: true,
        UniqueBack: false,
        Type: 0,
      };

      var cardCount = 0;
      var startID = deckID * 100;
      const cardID = startID + cardCount;
      const cardJson = {
        Name: "Card",
        Transform: {
          posX: 2.5,
          posY: 0,
          posZ: 5,
          rotX: 0,
          rotY: 180.0,
          rotZ: 0,
          scaleX: 1.0,
          scaleY: 1.0,
          scaleZ: 1.0,
        },
        Nickname: this.getCharacterNickname(this.deck),
        Description: this.getCharacterDescription(this.deck),
        GMNotes: "",
        ColorDiffuse: {
          r: 0.713235259,
          g: 0.713235259,
          b: 0.713235259,
        },
        Locked: false,
        Grid: true,
        Snap: true,
        IgnoreFoW: false,
        MeasureMovement: false,
        DragSelectable: true,
        Autoraise: true,
        Sticky: true,
        Tooltip: true,
        GridProjection: false,
        Hands: true,
        CardID: cardID,
        SidewaysCard: false,
        LuaScript: "",
        LuaScriptState: "",
        XmlUI: "",
        ContainedObjects: [],
        CustomDeck: customDeck,
      };
      ObjectStates.push(cardJson);
      cardCount++;
      this.deck.extraCharacters.forEach((card, idx) => {
        const cardID = startID + cardCount;
        const cardJson = {
          Name: "Card",
          Transform: {
            posX: 2.5 + 6 * (idx + 1),
            posY: 0,
            posZ: 5,
            rotX: 0,
            rotY: 180.0,
            rotZ: 0,
            scaleX: 1.0,
            scaleY: 1.0,
            scaleZ: 1.0,
          },
          Nickname: this.getCharacterNickname(card),
          Description: this.getCharacterDescription(card),
          GMNotes: "",
          ColorDiffuse: {
            r: 0.713235259,
            g: 0.713235259,
            b: 0.713235259,
          },
          Locked: false,
          Grid: true,
          Snap: true,
          IgnoreFoW: false,
          MeasureMovement: false,
          DragSelectable: true,
          Autoraise: true,
          Sticky: true,
          Tooltip: true,
          GridProjection: false,
          Hands: true,
          CardID: cardID,
          SidewaysCard: false,
          LuaScript: "",
          LuaScriptState: "",
          XmlUI: "",
          ContainedObjects: [],
          CustomDeck: customDeck,
        };
        ObjectStates.push(cardJson);
        cardCount++;
      });
    },
    generateRuleCards(deckID, ObjectStates) {
      const customDeck = {};

      const ruleDeckUrl = `${this.apiUrlBase}/api/card_img/${this.versionID}?width=${this.cardWidth}&deck=0&rules=1&columns=${this.cols}&cache=${this.cacheBuster}&language=${this.language}`;
      customDeck[`${deckID}`] = {
        FaceURL: ruleDeckUrl,
        BackURL: ruleDeckUrl,
        NumWidth: this.cols,
        NumHeight: this.ruleRows,
        BackIsHidden: true,
        UniqueBack: true,
        Type: 0,
      };

      var startID = deckID * 100;

      this.deck.ruleCards.forEach((card, idx) => {
        const cardID = startID + idx;
        const cardJson = {
          Name: "Card",
          Transform: {
            posX: -3,
            posY: 0,
            posZ: idx * -3,
            rotX: 0,
            rotY: 180.0,
            rotZ: 0,
            scaleX: 1.0,
            scaleY: 1.0,
            scaleZ: 1.0,
          },
          Nickname: card.title,
          Description: card.content,
          GMNotes: "",
          ColorDiffuse: {
            r: 0.713235259,
            g: 0.713235259,
            b: 0.713235259,
          },
          Locked: false,
          Grid: true,
          Snap: true,
          IgnoreFoW: false,
          MeasureMovement: false,
          DragSelectable: true,
          Autoraise: true,
          Sticky: true,
          Tooltip: true,
          GridProjection: false,
          Hands: true,
          CardID: cardID,
          SidewaysCard: false,
          LuaScript: "",
          LuaScriptState: "",
          XmlUI: "",
          ContainedObjects: [],
          CustomDeck: customDeck,
        };
        ObjectStates.push(cardJson);
      });
    },

    generateTokens(characterCard, ObjectStates, positionCounter = 0) {
      const xPos = 2.5 + 6 * positionCounter;
      const healthDialOffset = 2.5;
      const heroToken = {
        Name: "PlayerPawn",
        Transform: {
          posX: xPos,
          posY: 0,
          posZ: 0,
          rotX: 0,
          rotY: 90,
          rotZ: 0,
          scaleX: 2.35,
          scaleY: 2.35,
          scaleZ: 2.35,
        },
        Nickname: characterCard.hero.name,
        Description: "Hero",
        GMNotes: "",
        ColorDiffuse: this.colorDiffuse,
        Locked: false,
        Grid: true,
        Snap: true,
        IgnoreFoW: false,
        MeasureMovement: false,
        DragSelectable: true,
        Autoraise: true,
        Sticky: true,
        Tooltip: true,
        GridProjection: false,
        HideWhenFaceDown: false,
        Hands: false,
        MaterialIndex: 5,
        LuaScript: "",
        LuaScriptState: "",
        XmlUI: "",
      };

      ObjectStates.push(heroToken);

      const heroHealth = {
        Name: "Custom_Model",
        Transform: {
          posX: xPos + healthDialOffset,
          posY: 0,
          posZ: 0,
          rotX: 0,
          rotY: 180,
          rotZ: 0,
          scaleX: 1.0,
          scaleY: 1.0,
          scaleZ: 1.0,
        },
        Nickname: characterCard.hero.name,
        Description: "Hero Health",
        GMNotes: "",
        ColorDiffuse: this.colorDiffuse,
        Locked: false,
        Grid: true,
        Snap: true,
        IgnoreFoW: false,
        MeasureMovement: false,
        DragSelectable: true,
        Autoraise: true,
        Sticky: true,
        Tooltip: true,
        GridProjection: false,
        HideWhenFaceDown: false,
        Hands: false,
        CustomMesh: {
          MeshURL:
            "http://cloud-3.steamusercontent.com/ugc/1026203216033710122/3BEA746270C8F197B39EC4F13125C5B4AA590D61/",
          DiffuseURL: "",
          NormalURL: "",
          ColliderURL: "",
          Convex: true,
          MaterialIndex: 1,
          TypeIndex: 0,
          CustomShader: {
            SpecularColor: {
              r: 0.875,
              g: 0.812880337,
              b: 0.7463235,
            },
            SpecularIntensity: 0.0,
            SpecularSharpness: 4.0,
            FresnelStrength: 0.107142858,
          },
          CastShadows: true,
        },
        LuaScript:
          "CONFIG = {\r\n    MIN_VALUE = 0,\r\n    MAX_VALUE = " +
          characterCard.hero.hp +
          ',\r\n    FONT_COLOR = {0,0,0,100},\r\n    FONT_SIZE = 450,\r\n    LABEL_SIZE = 400,\r\n    LABEL_FONT_COLOR = {0,0,0,100},\r\n    LABEL_BG_COLOR = {100,100,100,10},\r\n    COLOR_HEX = "' +
          this.textColour +
          '",\r\n    TOOLTIP_SHOW = true,\r\n    VALUE = MAX_VALUE,\r\n}\r\n\r\nINJECTED_COUNTER = true\r\nfunction onload(saved_data)\r\n  CONFIG.VALUE = CONFIG.MAX_VALUE\r\n  Wait.time(createAll, 1, 0)\r\nend\r\nfunction hexToRGB(hex)\r\n    hex = hex:gsub("#","")\r\n    R = tonumber(hex:sub(1,2),16)/255\r\n    G = tonumber(hex:sub(3,4),16)/255\r\n    B = tonumber(hex:sub(5,6),16)/255\r\n    result = {r=R,g=G,b=B,a=100}\r\n    return result\r\nend\r\nfunction createAll()\r\n    if CONFIG.TOOLTIP_SHOW then\r\n        ttText = self.getName().. ": " .. CONFIG.VALUE\r\n    else\r\n        ttText = self.getName()\r\n    end\r\n    local scale = {x=1.5, y=1.5, z=1.5}\r\n    local thickness = self.getBoundsNormalized().size.y\r\n    if self.name == "Custom_Tile" then\r\n        thickness = thickness*2\r\n        scale = {0.75, 0.75, 0.75}\r\n    end\r\n    self.createButton({\r\n        label = tostring(CONFIG.VALUE),\r\n        click_function = "doNothing",\r\n        tooltip=ttText,\r\n        function_owner = self,\r\n        alignment = 3,\r\n        position = {0,thickness/2,-1.6},\r\n        width = 0,\r\n        height = 0,\r\n        font_size = CONFIG.LABEL_SIZE,\r\n        scale=scale,\r\n        font_color= hexToRGB(CONFIG.COLOR_HEX),\r\n        color = CONFIG.LABEL_BG_COLOR\r\n        })\r\n    self.createButton({\r\n        label=">",\r\n        click_function="add",\r\n        tooltip="+",\r\n        function_owner=self,\r\n        position={0.6, thickness/2, -1.7},\r\n        height=400,\r\n        width=200,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=CONFIG.FONT_SIZE,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n    self.createButton({\r\n        label="<",\r\n        click_function="subtract",\r\n        tooltip="-",\r\n        function_owner=self,\r\n        position={-0.6, thickness/2, -1.7},\r\n        height=400,\r\n        width=200,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=CONFIG.FONT_SIZE,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n    self.createButton({\r\n        label="RESET",\r\n        click_function="reset",\r\n        tooltip="Reset Health",\r\n        function_owner=self,\r\n        position={0, thickness/2, -2.5},\r\n        height=100,\r\n        width=400,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=20,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n\r\n    setTooltips()\r\nend\r\n\r\nfunction doNothing(_obj, _color, alt_click)\r\nend\r\n\r\nfunction editName(_obj, _string, value)\r\n    self.editInput({value = self.getName()})\r\n    setTooltips()\r\nend\r\n\r\nfunction add_subtract(_obj, _color, alt_click)\r\n    mod = alt_click and -1 or 1\r\n    new_value = math.min(math.max(CONFIG.VALUE + mod, CONFIG.MIN_VALUE), CONFIG.MAX_VALUE)\r\n    if CONFIG.VALUE ~= new_value then\r\n        CONFIG.VALUE = new_value\r\n        updateVal()\r\n    end\r\nend\r\n\r\nfunction add(_obj, _color, alt_click)\r\n    add_subtract(_obj, _color, false)\r\nend\r\n\r\nfunction subtract(_obj, _color, alt_click)\r\n    add_subtract(_obj, _color, true)\r\nend\r\n\r\nfunction reset(_obj, _color, alt_click)\r\n    CONFIG.VALUE = CONFIG.MAX_VALUE\r\n    updateVal()\r\nend\r\n\r\nfunction updateVal()\r\n    self.editButton({\r\n        index = 0,\r\n        label = tostring(CONFIG.VALUE)\r\n        })\r\n    setTooltips()\r\nend\r\n\r\nfunction setTooltips()\r\n    if CONFIG.TOOLTIP_SHOW then\r\n        ttText = self.getName().. ": " .. CONFIG.VALUE\r\n    else\r\n        ttText = self.getName()\r\n    end\r\n    self.editButton({\r\n        index = 0,\r\n        value = tostring(CONFIG.VALUE),\r\n        tooltip = ttText\r\n        })\r\nend',
        LuaScriptState: "",
        XmlUI: "",
      };
      ObjectStates.push(heroHealth);

      for (
        var sidekickNumber = 0;
        sidekickNumber < characterCard.sidekick.quantity;
        sidekickNumber++
      ) {
        const sidekickToken = {
          Name: "Custom_Tile",
          Transform: {
            posX: xPos,
            posY: 0,
            posZ: -4,
            rotX: 0,
            rotY: 0,
            rotZ: 0,
            scaleX: 0.75,
            scaleY: 0.75,
            scaleZ: 0.75,
          },
          Nickname: characterCard.sidekick.name,
          Description: "Sidekick Token",
          GMNotes: "",
          ColorDiffuse: this.colorDiffuse,
          Locked: false,
          Grid: true,
          Snap: true,
          IgnoreFoW: false,
          MeasureMovement: false,
          DragSelectable: true,
          Autoraise: true,
          Sticky: true,
          Tooltip: true,
          GridProjection: false,
          HideWhenFaceDown: false,
          Hands: false,
          CustomImage: {
            // "ImageURL": "http://cloud-3.steamusercontent.com/ugc/1675862637188914264/6C6A26FEFBFB46F4F77667ADA0FFAF1FE689A387/",
            // "ImageSecondaryURL": "http://cloud-3.steamusercontent.com/ugc/1675862637188914264/6C6A26FEFBFB46F4F77667ADA0FFAF1FE689A387/",
            ImageURL: `https://singlecolorimage.com/get/${this.deck.appearance.highlightColour.substring(
              1
            )}/1x1`,
            ImageSecondaryURL: `https://singlecolorimage.com/get/${this.deck.appearance.highlightColour.substring(
              1
            )}/1x1`,
            ImageScalar: 1.0,
            WidthScale: 0.0,
            CustomTile: {
              Type: 2,
              Thickness: 0.2,
              Stackable: false,
              Stretch: true,
            },
          },
          LuaScript: "",
          LuaScriptState: "",
          XmlUI: "",
        };
        ObjectStates.push(sidekickToken);

        if (characterCard.sidekick.quantity === 1) {
          const sidekickHealth = {
            Name: "Custom_Model",
            Transform: {
              posX: xPos + healthDialOffset,
              posY: 0,
              posZ: -4,
              rotX: 0,
              rotY: 180.000671,
              rotZ: 0,
              scaleX: 1.0,
              scaleY: 1.0,
              scaleZ: 1.0,
            },
            Nickname: characterCard.sidekick.name,
            Description: "Sidekick health",
            GMNotes: "",
            ColorDiffuse: this.colorDiffuse,
            Locked: false,
            Grid: true,
            Snap: true,
            IgnoreFoW: false,
            MeasureMovement: false,
            DragSelectable: true,
            Autoraise: true,
            Sticky: true,
            Tooltip: true,
            GridProjection: false,
            HideWhenFaceDown: false,
            Hands: false,
            CustomMesh: {
              MeshURL:
                "http://cloud-3.steamusercontent.com/ugc/1026203216033710122/3BEA746270C8F197B39EC4F13125C5B4AA590D61/",
              DiffuseURL: "",
              NormalURL: "",
              ColliderURL: "",
              Convex: true,
              MaterialIndex: 1,
              TypeIndex: 0,
              CustomShader: {
                SpecularColor: {
                  r: 0.875,
                  g: 0.812880337,
                  b: 0.7463235,
                },
                SpecularIntensity: 0.0,
                SpecularSharpness: 4.0,
                FresnelStrength: 0.107142858,
              },
              CastShadows: true,
            },
            LuaScript:
              "CONFIG = {\r\n    MIN_VALUE = 0,\r\n    MAX_VALUE = " +
              characterCard.sidekick.hp +
              ',\r\n    FONT_COLOR = {0,0,0,100},\r\n    FONT_SIZE = 450,\r\n    LABEL_SIZE = 400,\r\n    LABEL_FONT_COLOR = {0,0,0,100},\r\n    LABEL_BG_COLOR = {100,100,100,10},\r\n    COLOR_HEX = "' +
              this.textColour +
              '",\r\n    TOOLTIP_SHOW = true,\r\n    VALUE = MAX_VALUE,\r\n}\r\n\r\nINJECTED_COUNTER = true\r\nfunction onload(saved_data)\r\n  CONFIG.VALUE = CONFIG.MAX_VALUE\r\n  Wait.time(createAll, 1, 0)\r\nend\r\nfunction hexToRGB(hex)\r\n    hex = hex:gsub("#","")\r\n    R = tonumber(hex:sub(1,2),16)/255\r\n    G = tonumber(hex:sub(3,4),16)/255\r\n    B = tonumber(hex:sub(5,6),16)/255\r\n    result = {r=R,g=G,b=B,a=100}\r\n    return result\r\nend\r\nfunction createAll()\r\n    if CONFIG.TOOLTIP_SHOW then\r\n        ttText = self.getName().. ": " .. CONFIG.VALUE\r\n    else\r\n        ttText = self.getName()\r\n    end\r\n    local scale = {x=1.5, y=1.5, z=1.5}\r\n    local thickness = self.getBoundsNormalized().size.y\r\n    if self.name == "Custom_Tile" then\r\n        thickness = thickness*2\r\n        scale = {0.75, 0.75, 0.75}\r\n    end\r\n    self.createButton({\r\n        label = tostring(CONFIG.VALUE),\r\n        click_function = "doNothing",\r\n        tooltip=ttText,\r\n        function_owner = self,\r\n        alignment = 3,\r\n        position = {0,thickness/2,-1.6},\r\n        width = 0,\r\n        height = 0,\r\n        font_size = CONFIG.LABEL_SIZE,\r\n        scale=scale,\r\n        font_color= hexToRGB(CONFIG.COLOR_HEX),\r\n        color = CONFIG.LABEL_BG_COLOR\r\n        })\r\n    self.createButton({\r\n        label=">",\r\n        click_function="add",\r\n        tooltip="+",\r\n        function_owner=self,\r\n        position={0.6, thickness/2, -1.7},\r\n        height=400,\r\n        width=200,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=CONFIG.FONT_SIZE,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n    self.createButton({\r\n        label="<",\r\n        click_function="subtract",\r\n        tooltip="-",\r\n        function_owner=self,\r\n        position={-0.6, thickness/2, -1.7},\r\n        height=400,\r\n        width=200,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=CONFIG.FONT_SIZE,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n    self.createButton({\r\n        label="RESET",\r\n        click_function="reset",\r\n        tooltip="Reset Health",\r\n        function_owner=self,\r\n        position={0, thickness/2, -2.5},\r\n        height=100,\r\n        width=400,\r\n        alignment = 3,\r\n        scale=scale,\r\n        font_size=20,\r\n        font_color={0,0,0,0},\r\n        color={0,0,0,0}\r\n        })\r\n\r\n    setTooltips()\r\nend\r\n\r\nfunction doNothing(_obj, _color, alt_click)\r\nend\r\n\r\nfunction editName(_obj, _string, value)\r\n    self.editInput({value = self.getName()})\r\n    setTooltips()\r\nend\r\n\r\nfunction add_subtract(_obj, _color, alt_click)\r\n    mod = alt_click and -1 or 1\r\n    new_value = math.min(math.max(CONFIG.VALUE + mod, CONFIG.MIN_VALUE), CONFIG.MAX_VALUE)\r\n    if CONFIG.VALUE ~= new_value then\r\n        CONFIG.VALUE = new_value\r\n        updateVal()\r\n    end\r\nend\r\n\r\nfunction add(_obj, _color, alt_click)\r\n    add_subtract(_obj, _color, false)\r\nend\r\n\r\nfunction subtract(_obj, _color, alt_click)\r\n    add_subtract(_obj, _color, true)\r\nend\r\n\r\nfunction reset(_obj, _color, alt_click)\r\n    CONFIG.VALUE = CONFIG.MAX_VALUE\r\n    updateVal()\r\nend\r\n\r\nfunction updateVal()\r\n    self.editButton({\r\n        index = 0,\r\n        label = tostring(CONFIG.VALUE)\r\n        })\r\n    setTooltips()\r\nend\r\n\r\nfunction setTooltips()\r\n    if CONFIG.TOOLTIP_SHOW then\r\n        ttText = self.getName().. ": " .. CONFIG.VALUE\r\n    else\r\n        ttText = self.getName()\r\n    end\r\n    self.editButton({\r\n        index = 0,\r\n        value = tostring(CONFIG.VALUE),\r\n        tooltip = ttText\r\n        })\r\nend',
            LuaScriptState: "",
            XmlUI: "",
          };
          ObjectStates.push(sidekickHealth);
        }
      }
    },
  },
  asyncComputed: {
    async cardImageErrors() {
      const deckImageErrors = await api.checkDeckImages(this.versionID);
      return deckImageErrors;
    },
  },
  computed: {
    cardbackUrlPlaceholder() {
      return (
        this.deck.appearance.cardbackUrl || "URL of image to use for card back"
      );
    },
    totalCards() {
      return this.deck.cards.reduce((acc, card) => acc + card.quantity, 0);
    },
    rows() {
      return Math.ceil(this.deck.cards.length / this.cols);
    },
    charRows() {
      return Math.ceil((this.deck.extraCharacters.length + 1) / this.cols);
    },
    ruleRows() {
      return Math.ceil(this.deck.ruleCards.length / this.cols);
    },
    colorDiffuse() {
      const rgbColor = hexRgb(this.deck.appearance.highlightColour);
      return {
        r: rgbColor.red / 255,
        g: rgbColor.green / 255,
        b: rgbColor.blue / 255,
      };
    },
    textColour() {
      return best("#ffffff", "#000000", this.deck.appearance.highlightColour);
    },
    ttsJson() {
      // TODO DeckIDs array
      const deckID = 2;
      const characterCardDeckId = 3;
      const ruleCardDeckId = 3;

      const ObjectStates = [];

      ObjectStates.push(this.generateDeck(deckID));

      this.generateCharacterCards(characterCardDeckId, ObjectStates);
      if (this.deck.ruleCards.length)
        this.generateRuleCards(ruleCardDeckId, ObjectStates);

      this.generateTokens(this.deck, ObjectStates);

      this.deck.extraCharacters.forEach((character, idx) =>
        this.generateTokens(character, ObjectStates, idx + 1)
      );

      return {
        ObjectStates,
      };
    },
    dataStr() {
      return (
        "data:text/json;charset=utf-8," +
        encodeURIComponent(JSON.stringify(this.ttsJson, null, 2))
      );
    },
    filename() {
      return this.name + ".json";
    },
    warnings() {
      const warnings = [];

      if (this.deck.appearance.patternName) {
        warnings.push("Card background patterns are not currently supported.");
      }

      if (this.deck.appearance.isPNP) {
        warnings.push(
          '"Print and play" appearance is not currently supported. It will export with the "Production" appearance instead.'
        );
      }

      if (checkEmoji(this.deck)) {
        warnings.push("Emoji will not render properly.");
      }

      if (this.cardImageErrors?.length) {
        this.cardImageErrors.forEach((error) => {
          warnings.push(
            `The image for ${error.title} could not be loaded and will not be included in output`
          );
        });
      }

      return warnings;
    },
  },
};
</script>
